import { Channel } from 'channelx';
import { filter } from 'rxjs/operators';
import { reactive } from 'vue';
import { RouteLocationNormalizedLoaded, RouteRecord } from 'vue-router';
import { app } from '@/main';
import { Channels } from '@/app.manager/events-manager/Channels';
import { AccessStatus } from '@/app.manager/types';
import { RouteModel } from '@/modules/navigation/models/route.model';
import { RouteProvider } from '@/modules/navigation/providers/route.provider';
import { Section } from '@/modules/navigation/services/section';

let navigationProviderInstance: any;

const NETWORK_AVAILABILITY_CHECK_INTERVAL = 1000;
const DEFAULT_BUTTON_ENABLED_STATE = true;
const DEFAULT_DRAWER_ENABLED_STATE = true;

function getRouter() {
  return app.config.globalProperties.$router;
}

export class NavigationProvider {
  private readonly routeProvider!: RouteProvider;

  public detourSourceRoute : RouteLocationNormalizedLoaded | null | undefined;

  private updateButtonsState() {
    this.updateIsNextEnabled();
    this.updateIsBackEnabled();
    this.updateIsSkipEnabled();
  }

  private _isBackEnabled = DEFAULT_BUTTON_ENABLED_STATE;

  private updateIsBackEnabled() {
    const isRouteMatchSection = this.api.activeSection?.route?.routeName === getRouter().currentRoute.value.name;
    const preventedByRouteLock = !!this.api.activeSection?.lockPreviousRoutes && isRouteMatchSection;

    this.api.isBackEnabled = !this.api.activeSection?.isBackButtonDisabled && this.isBackAvailable && this._isBackEnabled && !preventedByRouteLock && this.isNetworkAvailable;
  }

  public get isBackEnabled() {
    return this._isBackEnabled;
  }

  public set isBackEnabled(isEnabled: boolean) {
    this._isBackEnabled = isEnabled;
    this.updateIsBackEnabled();
  }

  private isBackAvailable = true;

  private _isNextEnabled = DEFAULT_BUTTON_ENABLED_STATE;

  private isNetworkAvailable = true;

  private checkNetworkAvailabilityInterval!: any;

  private checkNetworkAvailability() {
    this.checkNetworkAvailabilityInterval = setInterval(() => {
      if (app.config.globalProperties?.$socket?.connected) {
        this.isNetworkAvailable = true;
        clearInterval(this.checkNetworkAvailabilityInterval as number);
        this.checkNetworkAvailabilityInterval = null;
        this.updateButtonsState();
      }
    }, NETWORK_AVAILABILITY_CHECK_INTERVAL);
  }

  private updateIsNextEnabled() {
    this.api.isNextEnabled = !this.realActiveSection?.isNextButtonDisabled && this._isNextEnabled && this.isNetworkAvailable;
  }

  public get isNextEnabled() {
    return this._isNextEnabled;
  }

  public set isNextEnabled(isEnabled: boolean) {
    this._isNextEnabled = isEnabled;
    this.updateIsNextEnabled();
  }

  private _isSkipEnabled: boolean = DEFAULT_BUTTON_ENABLED_STATE;

  public get isSkipEnabled() {
    return this._isSkipEnabled;
  }

  public set isSkipEnabled(isSkipEnabled: boolean) {
    this._isSkipEnabled = isSkipEnabled;
    this.updateIsSkipEnabled();
  }

  private updateIsSkipEnabled() {
    this.api.isSkipEnabled = this._isSkipEnabled && !!this.skipRoute;
  }

  private _skipRoute?: boolean | string;

  public set skipRoute(newSkipRoute: boolean | string | undefined) {
    this._skipRoute = newSkipRoute;
  }

  public get skipRoute(): boolean | string | undefined {
    if (typeof this._skipRoute === 'string') {
      return this._skipRoute;
    }

    return this.api.activeSection?.skipRoute || this._skipRoute;
  }

  private _loadingCounter = 0;

  public set isLoading(loadingFlag : boolean) {
    if (loadingFlag) {
      this._loadingCounter += 1;
    } else {
      this._loadingCounter = Math.max(this._loadingCounter - 1, 0);
    }
  }

  public get isLoading() {
    return this._loadingCounter > 0;
  }

  private _isDrawerEnabled = DEFAULT_DRAWER_ENABLED_STATE;

  public set isDrawerEnabled(isEnabled: boolean) {
    this._isDrawerEnabled = isEnabled;
  }

  public get isDrawerEnabled() {
    return !this.realActiveSection?.isNavDrawerDisabled && this._isDrawerEnabled && this.sections.length > 1;
  }

  private setglobalProgressStep() {
    this.api.globalProgressStep = this.sections.findIndex(section => section.name === this.api.activeSection?.name) + 1 || 1;
  }

  get hideStepsCount(): boolean {
    return !!this.realActiveSection?.isStepsHidden || this.sections.length <= 1;
  }

  isNavigationHidden = false;

  protected get workflowCompiler(): any {
    return app.config.globalProperties.$workflowManager.compiler;
  }

  protected get getState(): any {
    return this.workflowCompiler?.getState;
  }

  public get isAccessStatusCompleted() : boolean {
    return this.getState?.appStatus === AccessStatus.COMPLETED;
  }

  private get hideNavigationOnComplete() {
    return this.workflowCompiler?.getGlobals()?.configs?.pagesOnCompleted?.hideNavigation;
  }

  private get isNewNavbarConfig(): boolean {
    try {
      return app.config.globalProperties.$featuresSupport.test('navbarVersion');
    } catch {
      return false;
    }
  }

  constructor() {
    if (navigationProviderInstance) {
      // eslint-disable-next-line no-constructor-return
      return navigationProviderInstance;
    }

    this.routeProvider = RouteProvider.getInstance();

    navigationProviderInstance = reactive(this);

    // @ts-ignore
    getRouter().afterEach(this.onAfterEach.bind(this));

    Channel.use(Channels.navigationChannel)
      .pipe(filter(({ eventName }) => eventName === 'routeChangeComplete'))
      .consume(() => {
        this.setActiveSection();
        this.setglobalProgressStep();
        this.updateIsNextEnabled();
        this.updateIsBackEnabled();
        this.updateIsSkipEnabled();
      });
  }

  public onNetworkError() {
    this.isNetworkAvailable = false;
    if (!this.checkNetworkAvailabilityInterval) {
      this.checkNetworkAvailability();
    }
    this.updateButtonsState();
  }

  public onNetworkRestored() {
    if (this.isNetworkAvailable) { return; }
    this.isNetworkAvailable = true;
    if (this.checkNetworkAvailabilityInterval) {
      clearInterval(this.checkNetworkAvailabilityInterval as number);
      this.checkNetworkAvailabilityInterval = null;
    }
    this.updateButtonsState();
  }

  private calcIsNavigationHidden() {
    if (this.isAccessStatusCompleted && this.hideNavigationOnComplete) {
      return true;
    }

    return !!this.realActiveSection?.isNavigationHidden;
  }

  private resetState() {
    this._isNextEnabled = DEFAULT_BUTTON_ENABLED_STATE;
    this._isBackEnabled = DEFAULT_BUTTON_ENABLED_STATE;
    this._isSkipEnabled = DEFAULT_BUTTON_ENABLED_STATE;
    this._isDrawerEnabled = DEFAULT_DRAWER_ENABLED_STATE;
  }

  private onAfterEach(to: RouteRecord, _from: RouteRecord) {
    // "name" can be null on a first load when routes are not populated.
    if (!to?.name || to?.name === this.realActiveSection?.name) {
      return;
    }

    this.resetState();

    this.isBackAvailable = this.sections[0]?.name !== to.name;

    this.realActiveSection = this.applyBackwardCompatibility(this.flatSections.find(section => section.name === to.name) as Section);

    this.isNavigationHidden = this.calcIsNavigationHidden();
    this.isSkipEnabled = !!this.realActiveSection?.skipRoute;
  }

  private get flatSections() {
    const flat: Section[] = [];

    // @ts-ignore
    this.routeProvider.collection.each((item: any) => {
      const parentSection = new Section(item, getRouter());

      flat.push(parentSection);

      if (item.collection.isNotEmpty()) {
        // @ts-ignore
        item.collection.each((subRoute: RouteModel) => flat.push(new Section(subRoute, getRouter(), parentSection)));
      }
    });

    return flat;
  }

  public get sections() {
    const flat: Section[] = this.flatSections.filter(section => section.isEnabled);

    this.api.globalProgressTotal = flat.length || 1;

    this.enrichSections(flat);

    return flat;
  }

  // Public reactive API
  public api: any = reactive({
    activeSection: { },
    globalProgressStep: 0,
    globalProgressTotal: 1,
    isNextEnabled: false,
    isBackEnabled: false,
    isSkipEnabled: false,
  });

  /**
   * Return a section from the list of *visible* sections.
   *
   * For hidden children returns the parent instead.
   */
  private setActiveSection() {
    const activeVisibleSection = this.sections.find(section => section.isActive);

    // if no visible section is active - find the active child, and return its parent. Fallback to an actual section.
    this.api.activeSection = activeVisibleSection || this.flatSections.find(section => section.isActive)?.parentSection || this.flatSections.find(section => section.isActive);
  }

  public detourToRoute(routeKey: string) {
    this.detourSourceRoute = getRouter().currentRoute.value;
    this.isNextEnabled = true;
    getRouter().replace({ name: routeKey });
  }

  public exitDetourMode() {
    if (this.detourSourceRoute?.name) {
      getRouter().replace({ name: this.detourSourceRoute?.name, hash: this.detourSourceRoute?.hash });

      this.detourSourceRoute = null;

      return true;
    }

    return false;
  }

  /**
   * Checks if the route exist and could be navigated to
   * checking that the route is not hidden or bypassed
   */
  public isRouteVisible(routeKey: string) {
    const routeModal = this.routeProvider.resolveRoute(routeKey);

    return routeModal && !routeModal?.isBypassEnabled;
  }

  /**
   * Return a real active section
   */
  public realActiveSection: Section | undefined | null = null;

  private applyBackwardCompatibility(section: Section): Section {
    if (this.isNewNavbarConfig) {
      return section;
    }

    switch (section?.name) {
      case 'wait-page':
        section.isNavigationHidden = true;
        break;
      case 'quiz':
        section.isNextButtonDisabled = true;
        break;
      default:
    }

    return section;
  }

  private isActiveRouteReached(route: RouteModel, stateProgressPageName: undefined | string | RouteModel) {
    // all questionnaire pages marked as reached if finishedQuestionnaireFirstTime is true (fixes issue in restart flow)
    if (this.getState.finishedQuestionnaireFirstTime
      && route?.parent?.key?.startsWith('questionnaire')) {
      return true;
    }

    if (stateProgressPageName) {
      return route.isBeforeOrSame(stateProgressPageName);
    }

    return false;
  }

  private enrichSections(sections: Section[]) {
    let lockPreviousRoutes = false;
    let stateProgressPageName: undefined | string | RouteModel = this.getState?.progressPage?.name;

    if (!stateProgressPageName) {
      stateProgressPageName = this.routeProvider.collection.first();
    }

    let activeRouteReached = false;

    for (let i = sections.length - 1; i >= 0; i -= 1) {
      const section = sections[i];

      if (!activeRouteReached && this.isActiveRouteReached(section.route, stateProgressPageName)) {
        activeRouteReached = true;
      }

      if (lockPreviousRoutes) {
        section.isLocked = true;
        section.isEnabled = false;
      } else {
        section.isEnabled = this.getIsEnabled(section.route, activeRouteReached);
      }

      if (activeRouteReached) {
        section.wasReached = true;

        if (!section.isActive) {
          section.isCompleted = true;
        }

        this.setCompletionProgress(section);

        if (typeof section.sectionProgress === 'number') {
          section.isSkipped = section.sectionProgress === 0;
          section.isCompleted = section.sectionProgress === 100;
        }
      }

      if (activeRouteReached && section.lockPreviousRoutes) {
        lockPreviousRoutes = true;
      }
    }
  }

  private getIsEnabled(route: RouteModel, activeRouteReached: boolean) {
    // Allow by-click navigation if to-page is not guarded per config
    const isNotGuarded = !route.isGuarded;

    // Allow by-click navigation if the to-page goes before the progress-page
    const isBackwards = !!import.meta.env.VITE_NAVIGATION_GUARDS_SKIP || activeRouteReached;

    // Guard navigation initiated from NavBar only on user click
    return isNotGuarded || isBackwards;
  }

  private setCompletionProgress(section: Section) {
    const overview = this.workflowCompiler.appNodes?.questionnaire?.[section.key];

    let totalQuestions = 0;
    let totalAnswered = 0;

    if (!overview) {
      return;
    }

    overview.forEach((entry: any) => {
      if (!entry.intro) {
        totalQuestions += 1;

        if (entry?.value?.value && entry?.value?.value !== '' && entry?.value?.value !== -1) {
          totalAnswered += 1;
        }
      }
    });

    if (totalQuestions === 0) {
      section.sectionProgress = 100;
    } else {
      section.sectionProgress = Math.floor((totalAnswered / totalQuestions) * 100);
    }
  }

  // @Publisher('eventsChannel')
  public back() {
    Channel.use(Channels.eventsChannel).publish({ eventName: 'backClicked' });
  }

  // @Publisher('eventsChannel')
  public next() {
    Channel.use(Channels.eventsChannel).publish({ eventName: 'nextClicked' });
  }

  // @Publisher('eventsChannel')
  public skip() {
    Channel.use(Channels.eventsChannel).publish({ eventName: 'skipClicked', payload: { route: this.skipRoute } });
  }
}
